import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import { get, post, del } from './api'

/**
 * Passkey registration and sign-in, client side.
 *
 * Two round trips each: ask the server for options, hand them to the
 * authenticator, send the result back. The challenge id ties the two halves
 * together; the server deletes it as it reads it, so a captured response cannot
 * be replayed.
 */

export type Role = 'owner' | 'admin' | 'staff' | 'user'

export interface User {
  id: string
  handle: string
  displayName: string
  role: Role
  status: string
  locale: string
  capabilities: string[]
  /**
   * Created by a guardian and signs in with a PIN rather than a passkey.
   *
   * On the session, not looked up, so every authorisation decision can see it
   * without a second query — and so a check for it cannot be forgotten by a
   * component that only asked about the role.
   */
  isDelegate: boolean
}

export interface Credential {
  id: string
  deviceName: string
  syncedToProvider: boolean
  createdAt: number
  lastUsedAt: number | null
}

/**
 * Can this browser do WebAuthn AT ALL?
 *
 * This is the check that decides whether to show the sign-in UI, and it is
 * deliberately not the platform-authenticator one below.
 *
 * `isUserVerifyingPlatformAuthenticatorAvailable()` answers a narrower question:
 * is there a BUILT-IN authenticator — Touch ID, Windows Hello. It says nothing
 * about a hardware security key, a phone over QR, or a password manager
 * extension, all of which are perfectly good passkey authenticators.
 *
 * Gating the UI on it locks out a desktop with a YubiKey, told flatly that its
 * browser "cannot use passkeys". Found by an end-to-end test in a real browser
 * with no platform authenticator attached — which is exactly what a desktop is.
 */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential
}

/**
 * Is there a BUILT-IN authenticator? Used only to word the hint.
 *
 * "Use Face ID or your fingerprint" is right on a phone and wrong on a desktop
 * with a security key, and the difference is worth getting right — but it is
 * never a reason to hide the button.
 */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!passkeysSupported()) return false
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

export async function register(input: {
  handle: string
  displayName?: string
  email?: string
  setupToken?: string
  deviceName?: string
}): Promise<{ user: User; backedUp: boolean }> {
  const { challengeId, options } = await post<{ challengeId: string; options: never }>(
    '/api/auth/register/options',
    input,
  )
  const response = await startRegistration({ optionsJSON: options })
  return post('/api/auth/register/verify', { challengeId, response, deviceName: input.deviceName })
}

export async function signIn(handle?: string): Promise<{ user: User }> {
  const { challengeId, options } = await post<{ challengeId: string; options: never }>('/api/auth/login/options', {
    handle,
  })
  const response = await startAuthentication({ optionsJSON: options })
  return post('/api/auth/login/verify', { challengeId, response })
}

/**
 * Sign in a DELEGATE with a handle and a PIN.
 *
 * The one way into this app that is not a passkey, and deliberately narrow.
 * A delegate is created by a guardian, holds no capabilities, gets a seven-day
 * session rather than thirty, and locks after five wrong PINs.
 *
 * It exists because some people cannot hold a passkey on the device in front of
 * them — a shared kitchen tablet, and an app store reviewer, who has no
 * credential on our authenticator and no way to enrol one. Without this, a
 * reviewer meets a passkey prompt they cannot answer and rejects the app for
 * being unreviewable, which is App Store guideline 2.1 and Play's equivalent.
 *
 * The server answers ONE message for every kind of failure — unknown handle,
 * wrong PIN, locked, suspended, not-a-delegate — so this cannot be used to ask
 * whether an account exists.
 */
export async function signInWithPin(handle: string, pin: string): Promise<{ user: User }> {
  return post('/api/auth/pin', { handle: handle.trim().toLowerCase(), pin })
}

export async function signOut(): Promise<void> {
  await post('/api/auth/logout')
}

export async function me(): Promise<User | null> {
  try {
    const { user } = await get<{ user: User | null }>('/api/auth/me')
    return user
  } catch {
    return null
  }
}

export function credentials() {
  return get<{ credentials: Credential[]; recoverable: boolean; warning: string | null }>('/api/auth/credentials')
}

/** Enrol another passkey on this account — the actual recovery story. */
export async function addPasskey(deviceName?: string): Promise<{ backedUp: boolean }> {
  const { challengeId, options } = await post<{ challengeId: string; options: never }>(
    '/api/auth/credentials/options',
  )
  const response = await startRegistration({ optionsJSON: options })
  return post('/api/auth/credentials/verify', { challengeId, response, deviceName })
}

export function removePasskey(id: string) {
  return del<{ ok: true }>(`/api/auth/credentials/${encodeURIComponent(id)}`)
}
